import React from 'react';
import Select, { MultiValue, StylesConfig } from 'react-select';

interface BasketItem {
  id: number | string;
  label: string;
}

interface BasketSelectProps {
  label: string;
  availableItems: BasketItem[];
  selectedIds: (number | string)[];
  onChange: (selectedIds: (number | string)[]) => void;
  /** Current assignments not present in availableItems (e.g. filtered by location). */
  extraSelectedItems?: BasketItem[];
  required?: boolean;
  getItemLabel?: (item: BasketItem) => string;
  searchPlaceholder?: string;
  placeholder?: string;
}

interface SelectOption {
  value: number | string;
  label: string;
}

const idKey = (id: number | string) => String(id);

const BasketSelect: React.FC<BasketSelectProps> = ({
  label,
  availableItems,
  selectedIds,
  onChange,
  extraSelectedItems = [],
  required = false,
  getItemLabel = (item) => item.label,
  searchPlaceholder = 'Search...',
  placeholder = 'Select items...'
}) => {
  const options: SelectOption[] = React.useMemo(() => {
    const map = new Map<string, SelectOption>();
    availableItems.forEach(item => {
      map.set(idKey(item.id), { value: item.id, label: getItemLabel(item) });
    });
    extraSelectedItems.forEach(item => {
      const key = idKey(item.id);
      if (selectedIds.some(sid => idKey(sid) === key) && !map.has(key)) {
        map.set(key, { value: item.id, label: getItemLabel(item) });
      }
    });
    selectedIds.forEach(id => {
      const key = idKey(id);
      if (!map.has(key)) {
        map.set(key, { value: id, label: `#${id}` });
      }
    });
    const result: SelectOption[] = [];
    map.forEach((option) => result.push(option));
    return result;
  }, [availableItems, extraSelectedItems, selectedIds, getItemLabel]);

  const selectedOptions = selectedIds
    .map(id => options.find(option => idKey(option.value) === idKey(id)))
    .filter((o): o is SelectOption => o != null);

  const handleChange = (newValue: MultiValue<SelectOption>) => {
    const newSelectedIds = newValue.map(option => option.value);
    onChange(newSelectedIds);
  };

  /** Stays above modals that use z-[100]–z-[110] (e.g. edit filter dialog). */
  const MENU_PORTAL_Z = 120;

  // Custom styles for react-select
  const customStyles: StylesConfig<SelectOption, true> = {
    menuPortal: (base) => ({ ...base, zIndex: MENU_PORTAL_Z }),
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
      },
      minHeight: '38px',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#dbeafe',
      borderRadius: '6px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#1e40af',
      fontWeight: '500',
      padding: '2px 6px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#1e40af',
      borderRadius: '0 6px 6px 0',
      '&:hover': {
        backgroundColor: '#93c5fd',
        color: '#1e3a8a',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
    }),
    input: (provided) => ({
      ...provided,
      color: '#374151',
    }),
  };

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <Select<SelectOption, true>
        isMulti
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        styles={customStyles}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        isSearchable
        isClearable={false}
        className="react-select-container"
        classNamePrefix="react-select"
        noOptionsMessage={({ inputValue }) => 
          inputValue ? `No items found for "${inputValue}"` : 'No items available'
        }
      />
    </div>
  );
};

export default BasketSelect;
